// @systeme: interface
// Les réglages produit : chaque clé, sa description, sa valeur. On modifie une valeur
// à la fois ; le serveur refuse ce qui n'a pas la bonne forme et le dit.
import { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { ReglageAdmin } from '@foyer/shared';
import { Chargement } from '../../composants/Chargement';
import { useMessages } from '../../composants/Messages';
import { api } from '../../lib/api';

/** La valeur telle qu'on la tape : un texte pour une chaîne, du JSON sinon. */
function versTexte(valeur: unknown): string {
  return typeof valeur === 'string' ? valeur : JSON.stringify(valeur);
}

function depuisTexte(texte: string, original: unknown): unknown {
  if (typeof original === 'string') return texte;
  try {
    return JSON.parse(texte);
  } catch {
    return texte;
  }
}

function LigneReglage({ reglage }: { reglage: ReglageAdmin }) {
  const { dire, direErreur } = useMessages();
  const [courant, setCourant] = useState(reglage);
  const [texte, setTexte] = useState(versTexte(reglage.valeur));
  const secret = reglage.cle === 'llm_cle_api';
  const modifie = texte !== versTexte(courant.valeur);

  async function enregistrer() {
    try {
      const maj = await api.admin.modifierReglage(reglage.cle, depuisTexte(texte, courant.valeur));
      setCourant(maj);
      setTexte(versTexte(maj.valeur));
      dire('C’est réglé.');
    } catch (e) {
      direErreur(e);
    }
  }

  return (
    <Stack spacing={0.5}>
      <Typography sx={{ fontWeight: 600 }}>{reglage.cle}</Typography>
      <Typography variant="body2" color="text.secondary">
        {reglage.description}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          size="small"
          fullWidth
          multiline={reglage.cle === 'fonctionnalites'}
          type={secret ? 'password' : 'text'}
          value={texte}
          onChange={(e) => setTexte(e.target.value)}
          inputProps={{ 'aria-label': reglage.cle }}
          placeholder={secret ? 'Coller la nouvelle clé' : ''}
        />
        <Button variant="outlined" disabled={!modifie} onClick={() => void enregistrer()}>
          Enregistrer
        </Button>
      </Stack>
    </Stack>
  );
}

export function Reglages() {
  const { direErreur } = useMessages();
  const [reglages, setReglages] = useState<ReglageAdmin[] | undefined>();

  useEffect(() => {
    api.admin.reglages().then(setReglages).catch(direErreur);
  }, [direErreur]);

  if (!reglages) return <Chargement />;
  return (
    <Stack spacing={3}>
      {reglages.map((r) => (
        <LigneReglage key={r.cle} reglage={r} />
      ))}
    </Stack>
  );
}
